import { useEffect, useState } from "react"
import { providers, Signer } from "ethers"

import RewardFarmer from "@hfc/shared/utils/blockchain/contracts/RewardFarmer"
import getBlockchainConfig from "@hfc/shared/config/web3/getBlockchainConfig"
import { useMyPotatoesQuery } from "@hfc/shared/gql/generated/graphql-operations"

const useRewardFarmer = (
	account: string,
	signerOrProvider: Signer | providers.Provider,
) => {
	const {
		data: potato,
		error: potatoError,
		loading: isLoadingPotatoBalance,
		refetch: refetchPotatoBalance,
	} = useMyPotatoesQuery({
		variables: {
			where: {
				account_in: [account?.toLowerCase()],
			},
		},
	})
	// const {
	// 	data: farmers,
	// 	error: farmerError,
	// 	loading: isLoadingFarmers,
	// } = useErc1155TokensQuery({
	// 	variables: {
	// 		where: {
	// 			account_in: [account?.toLowerCase()],
	// 		},
	// 	},
	// })

	const potatoBalance = potato?.erc20Balances?.[0]?.value

	useEffect(() => {
		if (signerOrProvider) {
			refetchPotatoBalance()
		}
	}, [refetchPotatoBalance, signerOrProvider])

	const [state, setState] = useState({
		claimablePotatoes: 0,
		isLoadingRewardableBlocks: false,
		isClaimingPotato: false,
		isSuccess: null,
	})
	const {
		claimablePotatoes,
		isClaimingPotato,
		isLoadingRewardableBlocks,
		isSuccess,
	} = state

	const getRewardableBlocks = async () => {
		try {
			const rewardFarmer = new RewardFarmer(
				getBlockchainConfig().rewardFarmerAddress,
				signerOrProvider,
			)
			const tx = await rewardFarmer.getRewardableBlocks(0, 0)
			await (tx as any).wait()
		} catch (err) {
			console.error(err)
		} finally {
			setState({
				...state,
				isLoadingRewardableBlocks: false,
			})
		}
	}

	const claimStakingReward = async (id: number) => {
		try {
			setState({
				...state,
				isSuccess: null,
			})
			const rewardFarmer = new RewardFarmer(
				getBlockchainConfig().rewardFarmerAddress,
				signerOrProvider,
			)
			const tx = await rewardFarmer.claimStakingReward(0, id)
			setState({
				...state,
				isClaimingPotato: true,
			})

			await (tx as any).wait()
			setState({
				...state,
				isSuccess: true,
			})
		} catch (err) {
			console.error(err)
			const errorMessage = err?.data?.message || err?.message
			alert(errorMessage)
			setState({
				...state,
				isSuccess: false,
				isClaimingPotato: false,
			})
		}
	}

	const claimCharacterAirdrop = async (id: number) => {
		try {
			setState({
				...state,
				isSuccess: null,
			})
			const rewardFarmer = new RewardFarmer(
				getBlockchainConfig().rewardFarmerAddress,
				signerOrProvider,
			)
			const tx = await rewardFarmer.claimCharacterAirdrop(0, id)
			setState({
				...state,
				isClaimingPotato: true,
			})
			await (tx as any).wait()
			setState({
				...state,
				isSuccess: true,
			})
		} catch (err) {
			console.error(err)
			const errorMessage = err?.data?.message || err?.message
			alert(errorMessage)
			setState({
				...state,
				isSuccess: false,
				isClaimingPotato: false,
			})
		}
	}

	const claimCharacterAirdropBatch = async (ids: number[]) => {
		try {
			setState({
				...state,
				isSuccess: null,
			})
			const rewardFarmer = new RewardFarmer(
				getBlockchainConfig().rewardFarmerAddress,
				signerOrProvider,
			)
			const tx = await rewardFarmer.claimCharacterAirdropBatch(0, ids)
			setState({
				...state,
				isClaimingPotato: true,
			})
			await (tx as any).wait()
			setState({
				...state,
				isSuccess: true,
			})
		} catch (err) {
			console.error(err)
			const errorMessage = err?.data?.message || err?.message
			alert(errorMessage)
			setState({
				...state,
				isSuccess: false,
				isClaimingPotato: false,
			})
		}
	}

	const claimStakingRewardBatch = async (ids: number[]) => {
		try {
			setState({
				...state,
				isSuccess: null,
			})
			const rewardFarmer = new RewardFarmer(
				getBlockchainConfig().rewardFarmerAddress,
				signerOrProvider,
			)
			const tx = await rewardFarmer.claimStakingRewardBatch(0, ids)
			setState({
				...state,
				isClaimingPotato: true,
			})
			await (tx as any).wait()
			setState({
				...state,
				isSuccess: true,
			})
		} catch (err) {
			console.error(err)
			const errorMessage = err?.data?.message || err?.message
			alert(errorMessage)
			setState({
				...state,
				isSuccess: false,
				isClaimingPotato: false,
			})
		}
	}

	const getCharacterAirdropAllocationBatch = async (ids: number[]) => {
		const rewardFarmer = new RewardFarmer(
			getBlockchainConfig().rewardFarmerAddress,
			signerOrProvider,
		)
		const amounts = await rewardFarmer.getCharacterAirdropAllocationBatch(
			0,
			ids,
		)

		return amounts
	}

	const getAirdropAllocation = async (account: string) => {
		const rewardFarmer = new RewardFarmer(
			getBlockchainConfig().rewardFarmerAddress,
			signerOrProvider,
		)
		const amount = await rewardFarmer.getAirdropAllocation(account)
		return amount
	}

	const claimAirdrop = async () => {
		try {
			setState({
				...state,
				isSuccess: null,
			})
			const rewardFarmer = new RewardFarmer(
				getBlockchainConfig().rewardFarmerAddress,
				signerOrProvider,
			)
			const tx = await rewardFarmer.claimAirdrop()
			setState({
				...state,
				isClaimingPotato: true,
			})
			await (tx as any).wait()
			setState({
				...state,
				isSuccess: true,
			})
		} catch (err) {
			console.error(err)
			const errorMessage = err?.data?.message || err?.message
			alert(errorMessage)
			setState({
				...state,
				isSuccess: false,
				isClaimingPotato: false,
			})
		} finally {
			setTimeout(() => {
				setState({
					...state,
					isClaimingPotato: false,
				})
			}, 3000)
		}
	}

	const getAirdropAllocationBatch = async (accounts: string[]) => {
		const rewardFarmer = new RewardFarmer(
			getBlockchainConfig().rewardFarmerAddress,
			signerOrProvider,
		)
		const amounts = await rewardFarmer.getAirdropAllocationBatch(accounts)
		return amounts
	}

	return {
		isSuccess,
		error: potatoError,
		isLoadingPotatoBalance,
		isClaimingPotato,
		isLoadingRewardableBlocks,
		potatoBalance,
		getRewardableBlocks,
		claimablePotatoes,
		claimStakingReward,
		claimStakingRewardBatch,
		claimCharacterAirdrop,
		claimCharacterAirdropBatch,
		setState,
		refetchPotatoBalance,
		getCharacterAirdropAllocationBatch,
		getAirdropAllocation,
		getAirdropAllocationBatch,
		claimAirdrop,
	}
}

export default useRewardFarmer
